import { useEffect } from "react";

export const SITE_ORIGIN = "https://goliminl.com";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/assets/og-image.png`;

export type StructuredData = Record<string, unknown> | Record<string, unknown>[];

type PageMeta = {
  canonicalPath?: string;
  description: string;
  image?: string;
  noIndex?: boolean;
  structuredData?: StructuredData;
  title: string;
};

function setMeta(selector: string, attribute: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(url: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.href = url;
}

export function usePageMeta({
  canonicalPath,
  description,
  image = DEFAULT_OG_IMAGE,
  noIndex = false,
  structuredData,
  title,
}: PageMeta) {
  useEffect(() => {
    const canonical = canonicalPath
      ? new URL(canonicalPath, SITE_ORIGIN).toString()
      : window.location.href;
    const robots = noIndex ? "noindex, nofollow" : "index, follow";

    document.title = title;
    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[name="robots"]', "name", "robots", robots);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", "Liminl");
    setMeta('meta[property="og:url"]', "property", "og:url", canonical);
    setMeta('meta[property="og:image"]', "property", "og:image", image);
    setMeta('meta[property="og:image:width"]', "property", "og:image:width", "1200");
    setMeta('meta[property="og:image:height"]', "property", "og:image:height", "630");
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", image);
    if (canonicalPath) {
      setCanonical(canonical);
    } else if (noIndex) {
      document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.remove();
    }

    const scriptId = "liminl-page-structured-data";
    document.getElementById(scriptId)?.remove();
    if (structuredData) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [canonicalPath, description, image, noIndex, structuredData, title]);
}
